import type {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import type { z } from 'zod'
import { baseApi } from '../../../platform/api/base-api'
import type {
  DecisionCommand,
  PresenceCommand,
  Receipt,
  ReceiptCommand,
  StoryAction,
  StoryDecision,
  Timed,
  Visit,
} from '../domain/story'
import {
  decisionSchema,
  offersPageSchema,
  receiptSchema,
  visitSchema,
} from './engagement-contracts'

export function decode<T>(
  result: {
    data?: unknown
    error?: FetchBaseQueryError | undefined
    meta?: FetchBaseQueryMeta | undefined
  },
  schema: z.ZodType<T>,
) {
  if (result.error) {
    const retry = result.meta?.response?.headers.get('Retry-After')
    const retryAfterMs = retry
      ? /^\d+$/.test(retry)
        ? Number(retry) * 1000
        : Math.max(0, Date.parse(retry) - Date.now())
      : 0
    const error = result.error
    if (error.status === 429 && Number.isFinite(retryAfterMs)) {
      return {
        error: {
          ...error,
          data: {
            ...(typeof error.data === 'object' && error.data !== null
              ? error.data
              : {}),
            retryAfterMs,
          },
        },
      }
    }
    return { error }
  }
  const parsed = schema.safeParse(result.data)
  if (!parsed.success)
    return {
      error: {
        status: 'CUSTOM_ERROR' as const,
        error: 'STORY_CONTRACT_INVALID',
      },
    }
  const date = Date.parse(result.meta?.response?.headers.get('Date') ?? '')
  return {
    data: {
      value: parsed.data,
      serverTime: Number.isFinite(date) ? date : null,
    },
  }
}
export const engagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    marketingOffers: builder.query<
      Timed<{ id: string; endsAt: number }[]>,
      string
    >({
      queryFn: async (_account, _api, _options, request) => {
        const items: { id: string; endsAt: number }[] = []
        for (let page = 1; page <= 1000; page += 1) {
          const result = decode(
            await request({
              url: '/engagement/offers',
              params: { page, page_size: 100 },
              timeout: 5000,
            }),
            offersPageSchema,
          )
          if (result.error) return { error: result.error }
          items.push(...result.data.value.items)
          if (!result.data.value.has_more)
            return {
              data: { value: items, serverTime: result.data.serverTime },
            }
        }
        return {
          error: { status: 'CUSTOM_ERROR', error: 'STORY_CATALOG_TOO_LARGE' },
        }
      },
    }),
    manualMarketingStory: builder.mutation<
      Timed<StoryDecision>,
      DecisionCommand & { campaignId: string }
    >({
      queryFn: async (command, _api, _options, request) =>
        decode(
          await request({
            url: '/engagement/manual-stories',
            method: 'POST',
            timeout: 5000,
            headers: { 'Idempotency-Key': command.key },
            body: {
              schema_version: 1,
              client_id: command.clientId,
              visit_id: command.visitId,
              campaign_id: command.campaignId,
              platform: command.platform,
            },
          }),
          decisionSchema,
        ),
    }),
    storyVisit: builder.mutation<Timed<Visit>, PresenceCommand>({
      queryFn: async (command, _api, _options, request) =>
        decode(
          await request({
            url: '/engagement/visits',
            method: 'POST',
            keepalive: command.state === 'hidden',
            timeout: 5000,
            body: {
              schema_version: 1,
              client_id: command.clientId,
              sequence: command.sequence,
              state: command.state,
              timezone: command.timezone,
            },
          }),
          visitSchema,
        ),
    }),
    storyDecision: builder.mutation<Timed<StoryDecision>, DecisionCommand>({
      queryFn: async (command, _api, _options, request) =>
        decode(
          await request({
            url: '/engagement/story-decisions',
            method: 'POST',
            timeout: 5000,
            headers: { 'Idempotency-Key': command.key },
            body: {
              schema_version: 1,
              client_id: command.clientId,
              visit_id: command.visitId,
              platform: command.platform,
            },
          }),
          decisionSchema,
        ),
    }),
    storyImpression: builder.mutation<Timed<Receipt>, ReceiptCommand>({
      queryFn: async (command, _api, _options, request) =>
        decode(
          await request({
            url: '/engagement/impressions',
            method: 'POST',
            keepalive: true,
            timeout: 5000,
            body: {
              schema_version: 1,
              client_id: command.clientId,
              reservation_id: command.reservationId,
            },
          }),
          receiptSchema,
        ),
    }),
    storyInteraction: builder.mutation<
      Timed<Receipt>,
      ReceiptCommand & { action: StoryAction }
    >({
      queryFn: async (command, _api, _options, request) =>
        decode(
          await request({
            url: '/engagement/story-interactions',
            method: 'POST',
            keepalive: true,
            timeout: 5000,
            body: {
              schema_version: 1,
              client_id: command.clientId,
              reservation_id: command.reservationId,
              action: command.action,
            },
          }),
          receiptSchema,
        ),
    }),
  }),
})
